import type {
  FeedbackFilter,
  Indicator,
  MemberIndicatorGroup,
  MemberSummary,
  MyFeedback,
  ProgramSummary,
  SignalFilters,
  SignalKind,
  TrendDirection,
} from './types';

// The single place the backend "indicator" vocabulary meets the UI "signal" vocabulary.

export type Patient = {
  id: string;
  memberId: string;
  name: string;
  programs: Array<{id: string; name: string}>;
};

export type Signal = {
  id: string;
  occurredAt: string;
  headline: string;
  trend: {direction?: TrendDirection; text: string};
};

export type SignalGroup = {
  patient: Patient;
  signals: Array<Signal & {hidden: boolean}>;
};

export type ProgramOption = {value: string; label: string};

const TREND_DIRECTIONS: readonly TrendDirection[] = ['up', 'down', 'flat'];

// The API serves trend as untyped jsonb; narrow it before the UI reads it.
function toTrend(trend: Indicator['trend']): Signal['trend'] {
  return {
    direction: TREND_DIRECTIONS.find(
      (direction) => direction === trend.direction,
    ),
    text: typeof trend.text === 'string' ? trend.text : '',
  };
}

export function toPatient(member: MemberSummary): Patient {
  return {
    id: member.id,
    memberId: member.id,
    name: member.displayName,
    // Untyped jsonb on the wire; keep only well-formed entries
    programs: member.programs.flatMap((program) =>
      typeof program.id === 'string' && typeof program.name === 'string'
        ? [{id: program.id, name: program.name}]
        : [],
    ),
  };
}

export function toSignal(indicator: Indicator): Signal {
  return {
    id: indicator.id,
    occurredAt: indicator.computedAt,
    headline: indicator.headline,
    trend: toTrend(indicator.trend),
  };
}

// A signal is "reviewed" when the requester has feedback on it; "pending" when
// not. `all` matches everything.
export function indicatorMatchesFeedback(
  indicator: Indicator,
  feedback: FeedbackFilter,
): boolean {
  if (feedback === 'all') return true;
  return Boolean(indicator.myFeedback) === (feedback === 'reviewed');
}

function matchesFilters(
  indicator: Indicator,
  {kinds, feedback}: {kinds: string[]; feedback: FeedbackFilter},
): boolean {
  if (kinds.length > 0 && !kinds.includes(indicator.kind)) return false;
  return indicatorMatchesFeedback(indicator, feedback);
}

// Keeps only the signals matching the active review filter, dropping people left
// with none. `all` returns the groups untouched. Filtering is per-signal, so a
// person with a mix of reviewed and pending signals shows under both tabs.
export function filterGroupsByFeedback(
  groups: MemberIndicatorGroup[],
  feedback: FeedbackFilter,
): MemberIndicatorGroup[] {
  if (feedback === 'all') return groups;
  return groups
    .map((group) => ({
      ...group,
      indicators: group.indicators.filter((indicator) =>
        indicatorMatchesFeedback(indicator, feedback),
      ),
    }))
    .filter((group) => group.indicators.length > 0);
}

// Per-tab people counts over the loaded groups. `pending` + `reviewed` can exceed
// `all` because a mixed person is counted in both.
export function feedbackFilterCounts(
  groups: MemberIndicatorGroup[],
): Record<FeedbackFilter, number> {
  return {
    all: groups.length,
    pending: filterGroupsByFeedback(groups, 'pending').length,
    reviewed: filterGroupsByFeedback(groups, 'reviewed').length,
  };
}

// Non-matching signals are flagged, not dropped, so the card can count and reveal them.
export function partitionSignalGroups(
  groups: MemberIndicatorGroup[],
  filters: {kinds: string[]; feedback: FeedbackFilter},
): SignalGroup[] {
  return groups
    .map((group) => ({
      patient: toPatient(group.member),
      signals: group.indicators.map((indicator) => ({
        ...toSignal(indicator),
        hidden: !matchesFilters(indicator, filters),
      })),
    }))
    .filter((group) => group.signals.some((signal) => !signal.hidden));
}

export function hasActiveFilters(filters: SignalFilters): boolean {
  return (
    filters.program !== 'all' ||
    filters.feedback !== 'all' ||
    filters.kinds.length > 0
  );
}

// Fingerprint of every active filter; keying cards by it collapses them all on any filter change.
export function toFilterKey(filters: SignalFilters): string {
  return JSON.stringify([filters.program, filters.feedback, filters.kinds]);
}

export function toProgramOptions(programs: ProgramSummary[]): ProgramOption[] {
  return programs.map((program) => ({value: program.id, label: program.name}));
}

export function toKindOptions(kinds: SignalKind[]): ProgramOption[] {
  return kinds.map((kind) => ({value: kind.slug, label: kind.label}));
}

// Latest active feedback per signal, keyed by indicator id — drives the reviewed
// state and the edit pre-fill.
export function toMyFeedbackMap(
  groups: MemberIndicatorGroup[],
): Record<string, MyFeedback> {
  const map: Record<string, MyFeedback> = {};
  for (const group of groups) {
    for (const indicator of group.indicators) {
      if (indicator.myFeedback) map[indicator.id] = indicator.myFeedback;
    }
  }
  return map;
}
